import { Reference } from '@approvalmax/types';
import { useAtom, useAtomValue } from 'jotai';
import uniqueId from 'lodash/uniqueId';
import { useSelector } from 'modules/react-redux';
import { getNetSuiteContext } from 'pages/requestForm/selectors/netsuite/netSuiteSelectors';
import { useCallback, useEffect } from 'react';

import { checkJournalEntryLineIsEmpty, reorderLines, setJournalEntryLineCurrencyPrecision } from '../../helpers';
import {
    netSuiteJournalEntryFieldValuesState,
    netSuiteJournalEntryItemListState,
} from '../../NetSuiteJournalEntryRequestForm/NetSuiteJournalEntryRequestForm.states';
import {
    useChangeNetSuiteRequest,
    useCurrencyPrecision,
    useGetAndIncreaseItemLineNumber,
    useUpdateLineCustomField,
} from '..';
import {
    createDefaultJournalEntryLine,
    updateJournalEntryLineFieldValue,
} from './useNetSuiteJournalEntryLines.helpers';
import {
    JournalEntryLineItemUpdateHandler,
    JournalEntryMultiLineFieldUpdateHandler,
} from './useNetSuiteJournalEntryLines.types';
import { JournalEntryFieldUpdateHandler } from './useNetSuiteJournalEntryLines.types';

export const useNetSuiteJournalEntryLines = () => {
    const [journalEntryItemList, setJournalEntryItemList] = useAtom(netSuiteJournalEntryItemListState);

    const getAndIncreaseItemLineNumber = useGetAndIncreaseItemLineNumber();

    const changeNetSuiteRequest = useChangeNetSuiteRequest();

    const { currency } = useAtomValue(netSuiteJournalEntryFieldValuesState);

    const { currencies } = useSelector(getNetSuiteContext);
    const currencyPrecision = useCurrencyPrecision(currency);

    useEffect(() => {
        if (!journalEntryItemList.length) {
            const lineNumber = getAndIncreaseItemLineNumber();

            setJournalEntryItemList([createDefaultJournalEntryLine(lineNumber)]);
        }
    }, [getAndIncreaseItemLineNumber, journalEntryItemList.length, setJournalEntryItemList]);

    const onAddLine = useCallback(() => {
        const lineNumber = getAndIncreaseItemLineNumber();

        setJournalEntryItemList((current) => [...current, createDefaultJournalEntryLine(lineNumber)]);

        changeNetSuiteRequest();
    }, [getAndIncreaseItemLineNumber, setJournalEntryItemList, changeNetSuiteRequest]);

    const onReorderLines = useCallback(
        (oldIndex: number, newIndex: number) => {
            setJournalEntryItemList((current) => reorderLines(current, oldIndex, newIndex));

            changeNetSuiteRequest();
        },
        [setJournalEntryItemList, changeNetSuiteRequest]
    );

    const onRemoveLine = useCallback(
        (id: string) => {
            setJournalEntryItemList((current) => current.filter((item) => item.id !== id));

            changeNetSuiteRequest();
        },
        [setJournalEntryItemList, changeNetSuiteRequest]
    );

    const onCloneLine = useCallback(
        (id: string) => {
            setJournalEntryItemList((current) => {
                const targetLine = current.find((line) => line.id === id);

                if (!targetLine) {
                    return current;
                }

                const lineNumber = getAndIncreaseItemLineNumber();

                return [
                    ...current,
                    {
                        ...targetLine,
                        lineNumber,
                        id: uniqueId('netSuiteJournalEntryLine-'),
                    },
                ];
            });

            changeNetSuiteRequest();
        },
        [setJournalEntryItemList, changeNetSuiteRequest, getAndIncreaseItemLineNumber]
    );

    const onFieldUpdate = useCallback<JournalEntryFieldUpdateHandler>(
        (id, key, value) => {
            setJournalEntryItemList((current) =>
                current.map((line) => {
                    if (line.id !== id) {
                        return line;
                    }

                    return updateJournalEntryLineFieldValue(line, key, value, currencyPrecision);
                })
            );

            changeNetSuiteRequest();
        },
        [setJournalEntryItemList, changeNetSuiteRequest, currencyPrecision]
    );

    const onMultiLineFieldUpdate = useCallback<JournalEntryMultiLineFieldUpdateHandler>(
        (key, value) => {
            setJournalEntryItemList((current) =>
                current.map((line) => updateJournalEntryLineFieldValue(line, key, value, currencyPrecision))
            );

            changeNetSuiteRequest();
        },
        [setJournalEntryItemList, changeNetSuiteRequest, currencyPrecision]
    );

    const updateLineItem = useCallback<JournalEntryLineItemUpdateHandler>(
        (id, data) => {
            setJournalEntryItemList((current) =>
                current.map((line) => {
                    if (line.id !== id) {
                        return line;
                    }

                    const newLine = {
                        ...line,
                        ...data,
                    };

                    newLine.isEmpty = checkJournalEntryLineIsEmpty(newLine);

                    return newLine;
                })
            );

            changeNetSuiteRequest();
        },
        [setJournalEntryItemList, changeNetSuiteRequest]
    );

    const updateItemListOnCurrencyChange = useCallback(
        (newCurrency: Reference | null) => {
            setJournalEntryItemList((current) => {
                return current.map((item) => {
                    if (!newCurrency) {
                        return item;
                    }

                    const newCurrencyPrecision = currencies.find(({ id }) => id === newCurrency?.id)?.precision ?? 2;

                    if (currencyPrecision !== newCurrencyPrecision) {
                        return setJournalEntryLineCurrencyPrecision(item, newCurrencyPrecision);
                    }

                    return item;
                });
            });

            changeNetSuiteRequest();
        },
        [setJournalEntryItemList, changeNetSuiteRequest, currencies, currencyPrecision]
    );

    const updateCustomField = useUpdateLineCustomField(setJournalEntryItemList);

    return {
        onFieldUpdate,
        onMultiLineFieldUpdate,
        updateLineItem,
        updateItemListOnCurrencyChange,
        updateCustomField,
        onAddLine,
        onReorderLines,
        onRemoveLine,
        onCloneLine,
    };
};
